import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  Box, Typography, TextField, Button, CircularProgress, Chip,
  Grid, Card, CardMedia, CardContent, Link,
} from "@mui/material";
import StorefrontIcon from "@mui/icons-material/Storefront";
import LockIcon from "@mui/icons-material/Lock";
import ImageNotSupportedIcon from "@mui/icons-material/ImageNotSupported";
import SyncIcon from "@mui/icons-material/Sync";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

import { isEmbedded } from "../../../utils/shoplineEmbedded";

const BASE_URL = process.env.REACT_APP_SERVER_ENDPOINT || "";
const API = `${BASE_URL}/api/integrations/shopline`;
// Social post suggestions are cached by the Authority Engine; clear it when the
// catalog changes so new suggestions are built from the store's products.
const AUTHORITY_CACHE_KEY = "authority_engine_cache";

interface ShoplineStatus {
  connected: boolean;
  store_handle?: string;
  shop_name?: string;
  sync_status?: "syncing" | "completed" | "failed" | null;
  product_count?: number;
  last_synced_at?: string | null;
  sync_error?: string | null;
}

interface ShoplineProduct {
  id: string;
  title: string;
  handle?: string;
  images?: { src: string }[];
  image?: { src: string };
  variants?: { price: string }[];
}

// Accepts "mystore", "mystore.myshopline.com", or "https://mystore.myshopline.com/..."
// and always reduces it to the bare handle the API expects.
const extractStoreHandle = (input: string): string => {
  let h = input.trim();
  h = h.replace(/^https?:\/\//i, "");
  h = h.split("/")[0];
  h = h.replace(/\.myshopline\.com.*$/i, "");
  h = h.replace(/^www\./i, "");
  return h;
};

const timeAgo = (iso?: string | null) => {
  if (!iso) return "";
  const diff = Math.max(0, Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h ago`;
  return new Date(iso).toLocaleDateString();
};

const clearSuggestionCache = () => {
  try { localStorage.removeItem(AUTHORITY_CACHE_KEY); } catch { /* ignore */ }
};

const ConnectShopline: React.FC = () => {
  const [status, setStatus] = useState<ShoplineStatus>({ connected: false });
  const [statusLoaded, setStatusLoaded] = useState(false);
  const [storeHandle, setStoreHandle] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [syncRequested, setSyncRequested] = useState(false);

  // --- product preview state ---
  const [products, setProducts] = useState<ShoplineProduct[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [nextPageInfo, setNextPageInfo] = useState<string | null>(null);

  const prevSyncStatus = useRef<string | null | undefined>(null);

  const getEmail = () => localStorage.getItem("user_email") || "";

  const fetchStatus = useCallback(async () => {
    const email = getEmail();
    if (!email) {
      setStatusLoaded(true);
      return;
    }
    try {
      const body = new URLSearchParams({ email });
      const { data } = await axios.post(`${API}/status`, body, { withCredentials: true });
      setStatus(data);
    } catch (err) {
      console.error("Failed to fetch Shopline status", err);
    } finally {
      setStatusLoaded(true);
    }
  }, []);

  const fetchProducts = useCallback(async (pageInfo?: string | null) => {
    const email = getEmail();
    if (!email) return;
    setProductsLoading(true);
    try {
      const body = new URLSearchParams({ email });
      if (pageInfo) body.set("page_info", pageInfo);
      const { data } = await axios.post(`${API}/products`, body, { withCredentials: true });
      setProducts((prev) => (pageInfo ? [...prev, ...(data.products || [])] : data.products || []));
      setNextPageInfo(data.next_page_info || null);
    } catch (err) {
      console.error("Failed to fetch Shopline products", err);
      toast.error("Could not load products.");
    } finally {
      setProductsLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Load the product preview once we know the store is connected.
  useEffect(() => {
    if (status.connected) fetchProducts();
  }, [status.connected, fetchProducts]);

  // Poll while the catalog is being pushed to the content library (Weaviate).
  const isSyncing = status.sync_status === "syncing" || syncRequested;
  useEffect(() => {
    if (!status.connected || !isSyncing) return;
    const t = setInterval(fetchStatus, 4000);
    return () => clearInterval(t);
  }, [status.connected, isSyncing, fetchStatus]);

  // React to sync completion / failure
  useEffect(() => {
    const prev = prevSyncStatus.current;
    const cur = status.sync_status;
    if (prev === "syncing" && cur === "completed") {
      clearSuggestionCache();
      toast.success(`${status.product_count ?? 0} products added to your content library.`);
      setSyncRequested(false);
    } else if (prev === "syncing" && cur === "failed") {
      toast.error("Product sync failed. Try again.");
      setSyncRequested(false);
    }
    if (cur && cur !== "syncing") setSyncRequested(false);
    prevSyncStatus.current = cur;
  }, [status.sync_status, status.product_count]);

  // Detect return from OAuth / install flow
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("shopline") === "connected") {
      toast.success("Shopline store connected! Importing your products…");
      clearSuggestionCache();
      setSyncRequested(true);
      params.delete("shopline");
      params.delete("handle");
      const newSearch = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (newSearch ? `?${newSearch}` : ""));
      fetchStatus();
    } else if (params.get("shopline") === "error") {
      const reason = params.get("reason");
      toast.error(reason ? `Shopline connection failed: ${reason}` : "Shopline connection failed.");
      params.delete("shopline");
      params.delete("reason");
      params.delete("handle");
      const newSearch = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (newSearch ? `?${newSearch}` : ""));
    }
  }, [fetchStatus]);

  const handleConnect = async () => {
    const handle = extractStoreHandle(storeHandle);
    const email = getEmail();

    if (!handle) {
      toast.error("Enter your Shopline store handle first.");
      return;
    }
    if (!email) {
      toast.error("You need to be logged in to connect Shopline.");
      return;
    }

    setConnecting(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/start`,
        { store_handle: handle, email },
        { withCredentials: true }
      );
      if (data?.auth_url) {
        window.location.href = data.auth_url;
      } else {
        toast.error("Could not start Shopline authorization.");
        setConnecting(false);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Connection failed");
      setConnecting(false);
    }
  };

  const handleSync = async () => {
    const email = getEmail();
    setSyncRequested(true);
    try {
      const body = new URLSearchParams({ email });
      await axios.post(`${API}/sync`, body, { withCredentials: true });
      prevSyncStatus.current = "syncing";
      fetchStatus();
      fetchProducts();
    } catch (err: any) {
      setSyncRequested(false);
      toast.error(err?.response?.data?.detail || "Could not start product sync.");
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm(
      "Unlink this Shopline store from your Ocesha account?\n\n" +
      "Its products will be removed from your content library and won't be used for suggestions.\n" +
      "To remove the app from your store completely, uninstall it in SHOPLINE admin → Apps."
    )) return;
    const email = getEmail();
    try {
      const body = new URLSearchParams({ email });
      const { data } = await axios.post(`${API}/disconnect`, body, { withCredentials: true });
      toast.success(typeof data === "string" && data ? data : "Shopline store unlinked.");
      clearSuggestionCache();
      setProducts([]);
      setNextPageInfo(null);
      fetchStatus();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Disconnect failed");
    }
  };

  const productUrl = (p: ShoplineProduct) =>
    p.handle && status.store_handle ? `https://${status.store_handle}.myshopline.com/products/${p.handle}` : "";

  if (!statusLoaded) {
    return (
      <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const syncBox = () => {
    if (isSyncing) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={14} sx={{ color: "#4BA5EB" }} />
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)" }}>
            Importing products and images into your content library…
          </Typography>
        </Box>
      );
    }
    if (status.sync_status === "failed") {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ErrorOutlineIcon sx={{ fontSize: 18, color: "#F87171" }} />
          <Typography variant="body2" sx={{ color: "#FCA5A5" }}>
            Product sync failed{status.sync_error ? `: ${status.sync_error}` : ""}.
          </Typography>
        </Box>
      );
    }
    if (status.sync_status === "completed") {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CheckCircleIcon sx={{ fontSize: 18, color: "#22C55E" }} />
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)" }}>
            {status.product_count ?? 0} products in your content library · synced {timeAgo(status.last_synced_at)}
          </Typography>
        </Box>
      );
    }
    return (
      <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)" }}>
        Products haven't been imported yet.
      </Typography>
    );
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: "auto", p: 4 }}>

      {status.connected ? (
        <>
          <Box sx={{
            p: 3, borderRadius: 2, border: "1px solid rgba(34,197,94,0.4)",
            background: "rgba(34,197,94,0.08)", mb: 3,
          }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
              <Box>
                <Chip label="Connected" color="success" size="small" sx={{ mb: 1 }} />
                <Typography sx={{ fontWeight: 600 }}>
                  {status.shop_name || status.store_handle}
                </Typography>
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)", mb: 1.5 }}>
                  {status.store_handle}.myshopline.com
                </Typography>
                {syncBox()}
              </Box>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSync}
                  disabled={isSyncing}
                  startIcon={isSyncing ? <CircularProgress size={14} color="inherit" /> : <SyncIcon />}
                  sx={{ textTransform: "none" }}
                >
                  {isSyncing ? "Syncing…" : "Sync products now"}
                </Button>
                {!isEmbedded() && (
                  <Button variant="outlined" color="error" size="small" onClick={handleDisconnect} sx={{ textTransform: "none" }}>
                    Unlink store
                  </Button>
                )}
              </Box>
            </Box>
          </Box>

          {status.sync_status === "completed" && (status.product_count ?? 0) > 0 && (
            <Box sx={{
              display: "flex", alignItems: "center", gap: 1.5, p: 2, mb: 3, borderRadius: 2,
              background: "rgba(147,51,234,0.10)", border: "1px solid rgba(147,51,234,0.35)",
            }}>
              <AutoAwesomeIcon sx={{ color: "#C4B5FD" }} />
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)" }}>
                Social post suggestions now come from your store's products, with the product's own image and a link to its page.
                Changes to your products in SHOPLINE are synced automatically within a minute or two.
              </Typography>
            </Box>
          )}

          {/* --- Product preview (read-only) --- */}
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            Products {products.length > 0 && `(${products.length}${nextPageInfo ? "+" : ""})`}
          </Typography>

          {productsLoading && products.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : products.length === 0 ? (
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.5)" }}>
              No products found in this store yet.
            </Typography>
          ) : (
            <>
              <Grid container spacing={2}>
                {products.map((p) => {
                  const image = p.images?.[0]?.src || p.image?.src;
                  const price = p.variants?.[0]?.price;
                  const url = productUrl(p);
                  return (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={p.id}>
                      <Card sx={{
                        background: "rgba(255,255,255,0.03)",
                        border: "1px solid rgba(255,255,255,0.08)",
                        height: "100%",
                      }}>
                        {image ? (
                          <CardMedia component="img" height="140" image={image} alt={p.title} sx={{ objectFit: "cover" }} />
                        ) : (
                          <Box sx={{ height: 140, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.05)" }}>
                            <ImageNotSupportedIcon sx={{ color: "rgba(255,255,255,0.2)" }} />
                          </Box>
                        )}
                        <CardContent sx={{ p: 1.5 }}>
                          <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>{p.title}</Typography>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.5 }}>
                            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)" }}>
                              {price ? price : ""}
                            </Typography>
                            {url && (
                              <Link href={url} target="_blank" rel="noopener noreferrer" underline="hover"
                                    sx={{ fontSize: 12, color: "#4BA5EB", display: "inline-flex", alignItems: "center", gap: 0.3 }}>
                                View <OpenInNewIcon sx={{ fontSize: 12 }} />
                              </Link>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>

              {nextPageInfo && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => fetchProducts(nextPageInfo)}
                    disabled={productsLoading}
                    startIcon={productsLoading ? <CircularProgress size={14} color="inherit" /> : undefined}
                  >
                    {productsLoading ? "Loading…" : "Load more"}
                  </Button>
                </Box>
              )}
            </>
          )}
        </>
      ) : isEmbedded() ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 3, borderRadius: 2,
                   border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.03)" }}>
          <CircularProgress size={18} sx={{ color: "#4BA5EB" }} />
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)" }}>
            Setting up your SHOPLINE store… If this doesn't finish, re-open Ocesha from SHOPLINE admin → Apps.
          </Typography>
        </Box>
      ) : (
        <Box sx={{
          p: 3, borderRadius: 2, border: "1px solid rgba(255,255,255,0.12)",
          background: "rgba(255,255,255,0.03)", mb: 3,
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <StorefrontIcon sx={{ color: "#6366f1" }} />
            <Typography sx={{ fontWeight: 600 }}>Shopline</Typography>
          </Box>
          <TextField
            fullWidth
            size="small"
            label="Store handle"
            placeholder="mystore"
            helperText="From your store URL — mystore.myshopline.com"
            value={storeHandle}
            onChange={(e) => setStoreHandle(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Button
            variant="contained"
            onClick={handleConnect}
            disabled={connecting}
            startIcon={connecting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {connecting ? "Connecting…" : "Connect with Shopline"}
          </Button>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
            <LockIcon sx={{ fontSize: 16, color: "rgba(255,255,255,0.4)" }} />
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)" }}>
              You'll be redirected to Shopline to securely authorize access to your product catalog.
            </Typography>
          </Box>
          <Typography variant="caption" component="div" sx={{ color: "rgba(255,255,255,0.4)", mt: 1 }}>
            You can also install Ocesha from your SHOPLINE admin → Apps (SHOPLINE App Store).
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ConnectShopline;
